<script lang="ts">
	import StaticPicture from "./Picture/StaticPicture.svelte";
	import logo from "../../../core/assets/logo.svg";
	import fastestLogo from "../assets/images/fastest-logo.svg?raw";
	import inHarmony from "../../../core/assets/icons/in-harmony-vertiflex.svg?raw";
	import linkedin from "../../../core/assets/icons/linkedin.svg?raw";
	import youtube from "../../../core/assets/icons/youtube.svg?raw";
	import { appName } from "../../../core/schema/names/appName.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./Footer.translations.json";
	import Address from "./Address.svelte";
	import Link from "./Link.svelte";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import { getTenant } from "../getTenant.js";
	import Icon from "./Icon.svelte";
	import { formatPhoneNumber } from "../../../core/utils/formatPhoneNumber";
	import { contactPhoneNumber } from "../../../core/schema/contactPhoneNumber.js";
	import { getAllSolutions } from "../../../core/schema/solutions/solutionsContent.js";
	import type { SolutionContent } from "../../../core/schema/solutions/SolutionContent.js";
	import { SolutionGroup } from "../../../core/schema/SolutionGroup.js";
	import { solutionGroupNameMap } from "../../../core/schema/names/solutionGroupNameMap.js";
	import { ProductType, productTypeNameMap } from "../../../core/schema/ProductType.js";
	import { getProductsByTypePath } from "../../../core/schema/paths/getProductsByTypePath.js";
	import { gdprPathMap } from "../../../core/schema/paths/gdprPathMap";
	import { cookiesPathMap } from "../../../core/schema/paths/cookiesPathMap";
	import { serviceSupportPathMap } from "../../../core/schema/paths/serviceSupportPathMap";
	import { aboutPathMap } from "../../../core/schema/paths/aboutPathMap";
	import { contactPathMap } from "../../../core/schema/paths/contactPathMap";
	import { blogPathMap } from "../../../core/schema/paths/blogPathMap";
	import { ourApproachPathMap } from "../../../core/schema/paths/ourApproachPathMap";
	import { caseStudiesPathMap } from "../../../core/schema/paths/caseStudiesPathMap";
	import { solutionsPathMap } from "../../../core/schema/paths/solutionsPathMap";
	import { getSolutionDetailPath } from "../../../core/schema/paths/getSolutionDetailPath";
	import { homepagePathMap } from "../../../core/schema/paths/homepagePathMap.js";

	const translate = getTranslate(translations);
	const tenant = getTenant();

	const solutions: SolutionContent[] = getAllSolutions();

	const emailValue = translate("emailValue");
	const contactDetails: Record<string, string>[] = [
		{
			name: translate("phone"),
			value: formatPhoneNumber(contactPhoneNumber),
			urlSlug: `tel:${contactPhoneNumber}`,
		},
		{
			name: translate("email"),
			value: emailValue,
			urlSlug: `mailto:${emailValue}`,
		},
	];

	const approachLinks = [
		{ label: translate("ourApproachHeadline"), href: `/${ourApproachPathMap[tenant.locale]}` },
		{ label: translate("solutionsHeadline"), href: `/${solutionsPathMap[tenant.locale]}` },
		{ label: translate("serviceHeadline"), href: `/${serviceSupportPathMap[tenant.locale]}` },
		{ label: translate("caseStudiesHeadline"), href: `/${caseStudiesPathMap[tenant.locale]}` },
		{ label: translate("aboutHeadline"), href: `/${aboutPathMap[tenant.locale]}` },
		{ label: translate("blogHeadline"), href: `/${blogPathMap[tenant.locale]}` },
		{ label: translate("contactHeadline"), href: `/${contactPathMap[tenant.locale]}` },
	];

	const socials = [
		{
			title: "LinkedIn",
			urlSlug: "https://www.linkedin.com/company/logtech-cz/home/",
			icon: linkedin,
		},
		{
			title: "YouTube",
			urlSlug: "https://www.youtube.com/@logtech8602",
			icon: youtube,
		},
	];

	const displayOrder: SolutionGroup[] = [
		SolutionGroup.ByDesignation,
		SolutionGroup.ByTechnology,
		SolutionGroup.ByIndustry,
		SolutionGroup.ByProduct,
	];
	const groupedSolutions = displayOrder.reduce<Partial<Record<SolutionGroup, SolutionContent[]>>>(
		(grouped, group) => ({
			...grouped,
			[group]: solutions.filter((s) => s.group === group),
		}),
		{},
	);
	const productTypes: ProductType[] = Object.values(ProductType);
</script>

<footer class="text-xs">
	<div class="bg-ltprimary">
		<div class="mx-auto max-w-[120rem]">
			<div class="flex items-center justify-between p-8 md:!px-12 md:!pt-12 md:pb-12 lg:!px-32 lg:!pt-16 lg:pb-16">
				<a href="/{homepagePathMap[tenant.locale]}">
					<StaticPicture
						alt="Logo {appName}"
						image={logo}
						class="h-6 w-auto xl:h-8"
						imgClass="w-fit h-full"
						loading="eager"
						width={227}
						height={25}
					/>
				</a>
				<Icon icon={inHarmony} class="size-20" role="img" aria-label="Vertiflex in harmony with LogTech" lang="en" />
			</div>
		</div>
		<div class="border-ltprimary-300 border-y">
			<div class="mx-auto max-w-[120rem] px-8 md:!px-12 lg:!px-32">
				<div class="border-ltprimary-300 grid items-start border-x text-left xl:grid-cols-2 2xl:grid-cols-[50rem_1fr]">
					<div class="border-ltprimary-300 grid h-full md:grid-cols-2 xl:border-r">
						<!-- Kontakt -->
						<div class="order-1 flex h-full flex-col p-8 md:order-none">
							<div class="mb-5">
								<div class="text-4xs mb-6 font-light uppercase tracking-wider text-white">
									{translate("contactHeadline")}
								</div>
								<Address class="font-sm/[1.5] font-normal" showHeadquarterState={false} />
							</div>
							<div class="mb-5">
								{#each contactDetails as detail}
									<div class="flex gap-1 font-normal leading-6">
										<span class="whitespace-nowrap text-white">{detail.name}:</span>
										<a class="text-white underline decoration-white" href={detail.urlSlug}>
											{nbspify(detail.value)}
										</a>
									</div>
								{/each}
							</div>
							<div class="mt-2 text-sm/normal font-normal text-white">{translate("companyRegistration")}</div>
							<div class="mt-auto flex items-end pt-4 xl:pt-[8.5rem]">
								{#each socials as social}
									<Link aria-label={social.title} href={social.urlSlug} target="_blank" class="group">
										<div class="rounded-2xl border border-white p-2">
											<Icon
												icon={social.icon}
												class="size-4 text-white transition-transform duration-500 ease-in-out group-hover:scale-[1.2]"
											/>
										</div>
									</Link>
								{/each}
							</div>
						</div>

						<!-- Kam dál -->
						<div class="md:border-ltprimary-300 order-1 p-8 md:order-none md:border-l">
							<div class="text-4xs mb-6 font-light uppercase tracking-wider text-white">
								{translate("navigateHeadline")}
							</div>
							<ul class="flex flex-col gap-4 xl:gap-8">
								{#each approachLinks as link}
									<li>
										<Link href={link.href} class="text-sm/none !font-normal uppercase tracking-wider text-white"
											>{link.label}</Link
										>
									</li>
								{/each}
							</ul>
						</div>
					</div>

					<!-- Naše řešení -->
					<div class="p-8">
						<div class="text-4xs mb-6 font-light uppercase tracking-wider text-white md:mt-0">
							{translate("solutionHeadline")}
						</div>
						<div class="grid grid-cols-1 gap-x-8 gap-y-2 md:grid-cols-2 lg:grid-cols-4">
							{#each displayOrder as group}
								{@const groupWords = solutionGroupNameMap[group][tenant.locale].split(" ")}
								{@const groupMid = Math.trunc(groupWords.length / 2)}
								{@const isProductGroup = group === SolutionGroup.ByProduct}
								<div class="row-span-2 grid grid-rows-subgrid">
									<div class="mb-2 mt-6 text-sm font-bold uppercase text-white first:mt-0 md:mt-0">
										<span class="block">{groupWords.slice(0, groupMid).join(" ")}</span>
										<span class="block">{groupWords.slice(groupMid).join(" ")}</span>
									</div>
									<ul class="mb-4 flex flex-col">
										{#if isProductGroup}
											{#each productTypes as productType (productType)}
												<li class="mb-2 text-sm/normal font-light text-white">
													<Link
														href="/{getProductsByTypePath(productType)[tenant.locale]}"
														class="!font-light text-white"
													>
														{productTypeNameMap[productType][tenant.locale]}
													</Link>
												</li>
											{/each}
										{:else}
											{#each groupedSolutions[group] ?? [] as solution (solution.slug)}
												<li class="mb-2 text-sm/normal font-light text-white">
													<Link href="/{getSolutionDetailPath(solution)[tenant.locale]}" class="!font-light text-white">
														{solution.name[tenant.locale]}
													</Link>
												</li>
											{/each}
										{/if}
									</ul>
								</div>
							{/each}
						</div>
					</div>
				</div>
			</div>
		</div>

		<div class="mx-auto flex max-w-[120rem] flex-col gap-8 py-12 md:gap-12 lg:gap-16">
			<div
				class="flex flex-col justify-between gap-8 px-8 text-white md:mt-8 md:flex-row md:items-center md:gap-0 md:!px-12 lg:!px-32 xl:mt-0"
			>
				<div class="text-2xs order-2 mb-0 font-normal tracking-wider md:order-1">{translate("copyright")}</div>
				<div class="order-1 flex md:order-2">
					<Link href="/sitemap.xml" class="text-2xs !font-normal text-white">
						{translate("sitemap")}
					</Link>
					<Link href="/{cookiesPathMap[tenant.locale]}" class="text-2xs !font-normal text-white">
						{translate("cookies")}
					</Link>
					<Link href="/{gdprPathMap[tenant.locale]}" class="text-2xs !font-normal text-white">
						{translate("gdpr")}
					</Link>
				</div>
			</div>
			<div class="inline-flex items-center gap-2 px-8 md:!px-12 lg:!px-32">
				<span class="text-xs/normal tracking-wider text-white">{translate("fastest")}</span>
				<a href="https://www.fastest.cz" target="_blank" aria-label="Fastest">
					<Icon icon={fastestLogo} class="-ml-2 mb-1 h-3 w-fit sm:mb-1 sm:ml-0 sm:h-4" />
				</a>
			</div>
		</div>
	</div>
</footer>
